import type { SVGProps } from "react";

/**
 * Status glyphs for the transcript.
 *
 * Drawn on the same 16 px grid and 1.5 px stroke as ui/Symbole.tsx so the panel does
 * not read as a different product. They live here rather than in the shared set
 * because they are only meaningful next to a step of machine work: a plan item, a tool
 * call, a delegated batch. Each one always sits beside a word — a shape carries the
 * state at a glance, the word carries it in greyscale and to a screen reader.
 */

type Props = SVGProps<SVGSVGElement> & { size?: number };

function Glyph({ size = 12, children, ...rest }: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

/** Step done. */
export const ZeichenHaken = (p: Props) => (
  <Glyph {...p}>
    <path d="M3 8.5 6.5 12 13 4.5" />
  </Glyph>
);

/** Step not started. */
export const ZeichenKasten = (p: Props) => (
  <Glyph {...p}>
    <rect x="3" y="3" width="10" height="10" rx="1" />
  </Glyph>
);

/** Step in flight. A filled centre reads as "occupied" without animating. */
export const ZeichenLaeuft = (p: Props) => (
  <Glyph {...p}>
    <rect x="3" y="3" width="10" height="10" rx="1" />
    <rect x="6" y="6" width="4" height="4" fill="currentColor" stroke="none" />
  </Glyph>
);

/** Step deliberately not taken. Distinct from done, because it is not done. */
export const ZeichenUebersprungen = (p: Props) => (
  <Glyph {...p}>
    <rect x="3" y="3" width="10" height="10" rx="1" />
    <path d="M5.5 8h5" />
  </Glyph>
);

/** Step failed. */
export const ZeichenKreuz = (p: Props) => (
  <Glyph {...p}>
    <path d="M4 4l8 8M12 4l-8 8" />
  </Glyph>
);

/** Disclosure. Rotated 90° by the caller when the row is open. */
export const ZeichenWinkel = (p: Props) => (
  <Glyph {...p}>
    <path d="M6 3.5 10.5 8 6 12.5" />
  </Glyph>
);

/** A gate. Used where a run stopped for a signature. */
export const ZeichenSchloss = (p: Props) => (
  <Glyph {...p}>
    <rect x="3" y="7" width="10" height="6.5" rx="1" />
    <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" />
  </Glyph>
);
